import {
	computeContextMenuTitle,
	ContextMenu,
	DateString,
	GridView,
	Icon,
	PipeSeparator,
	Space,
	useContextMenu
} from "client/Components";
import {
	MultiSelectableOptions,
	SelectableItemOptions,
	useUserTimezone
} from "client/Hooks";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import {
	GridItem as GridItemModel
} from "./SharePointRecentlyModified";
import {
	GridViewGroupItem,
	GridViewItemKind
} from "client/Components/GridView/groupGridViewItems";
import { DocumentGridViewItem } from "../../DocumentGridViewItem";
import { TimeZone } from "client/DateAndTime/timezones";
import { isNotNil } from "client/Misc/utils";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { DocumentGridViewContainer } from "../../DocumentGridViewContainer";
import { RecentDocumentAction, RecentDocumentActionContext } from "./actions";
import { RecentDocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { getSharePointItemPreviewUrl } from "../SharePointGridView";
import { DocumentNoResult } from "../../DocumentNoResult";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { localize } from "client_legacy/ndaw/culture";

export type SharePointRecentlyModifiedGridViewHandle = {
	resetScrollPosition: () => void;
};

export type SharePointRecentlyModifiedGridViewProps = {
	items: readonly GridItemModel[];
	multiSelection: MultiSelectableOptions;
	selectionActions: readonly RecentDocumentAction[];
	actionContext: RecentDocumentActionContext;
	isInSelectMode: boolean;
	isMobile: boolean;
	isTouch: boolean;
	hasSearch: boolean;
	selectionFor: (item: GridItemModel) => SelectableItemOptions;
	onOpenItem: (item: RecentDocumentItemViewModel) => void;
	onEnableSelectMode: () => void;
};

export const SharePointRecentlyModifiedGridView = forwardRef(
	(props: SharePointRecentlyModifiedGridViewProps, ref: Ref<SharePointRecentlyModifiedGridViewHandle>): ReactElement => {
		const {
			items,
			multiSelection,
			selectionActions,
			actionContext,
			isInSelectMode,
			isMobile,
			isTouch,
			hasSearch,
			selectionFor,
			onOpenItem,
			onEnableSelectMode
		} = props;

		const userTimezone: TimeZone = useUserTimezone();
		const gridRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const [contextMenuHandler, contextMenuProps] = useContextMenu(gridRef);

		const { doesSupportThumbnails } = documentsConfiguration()
			.sharePoint;

		const itemsToRender = useMemo(
			(): readonly GridViewGroupItem<GridItemModel>[] => {
				return items.map(
					x => ({
						model: x,
						uniqueKey: x.uniqueKey,
						itemKind: GridViewItemKind.Preview
					})
				);
			},
			[items]
		);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					gridRef.current
						?.scrollTo(0, 0);
				}
			}),
			[]
		);

		if (items.length === 0) {
			return (
				<DocumentNoResult
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<DocumentGridViewContainer>
				<GridView
					trackingId="share-point-recent-documents-grid-view"
					ref={gridRef}
					items={itemsToRender}
					multiSelection={multiSelection}
					hasLassoSelection={!isMobile}
					elementToRenderAfterItems={
						itemsToRender.length > 0
							? <AiAssistantFabSpacing />
							: undefined
					}
				>
					{item => {
						const {
							itemKind,
							model,
							uniqueKey
						} = item;

						const previewUrl: string | undefined = doesSupportThumbnails
							? getSharePointItemPreviewUrl(model.id, model.model.location)
							: undefined;

						return (
							<DocumentGridViewItem
								key={uniqueKey}
								id={model.id}
								name={model.name}
								additionalInfo={(
									<Info
										modifiedBy={model.modifiedBy}
										modifiedOn={model.modifiedOn}
										size={model.size}
										timezone={userTimezone}
									/>
								)}
								isBookmarked={Boolean(model.model.bookmarkId)}
								previewUrl={previewUrl}
								selectable={selectionFor(model)}
								documentKind={model.model
									.itemType}
								isMobile={isMobile}
								selectionActions={selectionActions}
								actionContext={{...actionContext, selection: [model.model]}}
								isTouch={isTouch}
								isPreview={itemKind === GridViewItemKind.Preview}
								isInSelectMode={isInSelectMode}
								onContextMenu={contextMenuHandler}
								onOpenItem={() => onOpenItem(model.model)}
								onEnableSelectMode={onEnableSelectMode}
							/>
						);
					}}
				</GridView>
				{contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={selectionActions}
						context={actionContext}
						trackingId="sharepoint-recently-modified-context-menu"
						title={computeContextMenuTitle(
							actionContext.selection,
							(x) => x.name
						)}
					/>
				)}
			</DocumentGridViewContainer>
		);
	}
);
SharePointRecentlyModifiedGridView.displayName = "SharePointRecentlyModifiedGridView";

type InfoProps = {
	modifiedOn: string | null;
	size: number | nil;
	timezone: TimeZone;
	modifiedBy: string | null;
};

export const Info = (props: InfoProps): ReactElement => {
	const {
		modifiedOn,
		size,
		timezone,
		modifiedBy
	} = props;

	return (
		<>
			<Icon value="pen" />
			{isNotNil(modifiedOn) && (
				<>
					<Space />
					<DateString
						format="RelativeTimestamp"
						timezone={timezone}
						value={modifiedOn}
					/>
				</>
			)}
			{isNotNil(modifiedBy) && (
				<>
					<Space />
					{localize("_(Edited)By")}
					<Space />
					{modifiedBy}
				</>
			)}
			{isNotNil(size) && (
				<>
					<PipeSeparator />
					{getDisplayFriendlySize(size)}
				</>
			)}
		</>
	);
};

Info.displayName = "Info";